<template>
  <v-row justify="center">
    <v-col cols="12" style="max-width:680px">
      <v-sheet class="pt-6">
        <h1 class="display-2 font-weight-bold d-inline-block mr-4"
          >Take a Tour of Minimart</h1
        >
        <div class="my-8">
          <cld-video
            publicId="v1635270255/minimart/Minimart_Demo_Video_Oct_2021-IG_1080"
            :style="{
              'max-width': '375px',
              'margin-left': ['xs', 'sm'].includes($mq) ? 'auto' : '0',
              'margin-right': ['xs', 'sm'].includes($mq) ? 'auto' : '0',
            }"
            width="100%"
            :fluid="true"
            :controls="false"
            :muted="true"
            :controlBar="{
              volumePanel: false,
            }"
            :colors="{
              accent: '#fdc05d',
            }"
            :floatingWhenNotVisible="false"
            :hideContextMenu="true"
            autoplayMode="on-scroll"
            loop="true"
            :autoplay="true"
          >
            <cld-transformation
              effect="accelerate:0"
              quality="50"
              format="auto"
            />
          </cld-video>
        </div>
        <Markdown
          v-if="getShopDetail('Features Intro').length"
          class="title my-4"
          :source="getShopDetail('Features Intro')"
        />
        <v-row wrap class="features">
          <v-col class="pb-7">
            <ul
              v-if="featureTitles"
              v-intersect="{
                handler: onIntersect,
                options: {
                  threshold: [0, 0.5, 1.0],
                },
              }"
            >
              <v-row>
                <v-col cols="11" class="mx-auto">
                  <li
                    v-for="(title, i) of featureTitles"
                    :key="title"
                    class="mt-3"
                  >
                    <div
                      :class="[
                        'feature-item mt-3',
                        visibleFeatures > i ? 'show' : 'hide',
                      ]"
                    >
                      <v-icon class="mb-2 " color="green" left
                        >mdi-check</v-icon
                      >
                      <h2
                        class="font-weight-bold d-inline-block"
                        @mouseover="hoveringTitle = title"
                        @mouseleave="hoveringTitle = null"
                        ><a v-scroll-to="`#${slugMe(title)}`"> {{ title }} </a
                        ><v-btn
                          small
                          icon
                          v-if="hoveringTitle === title"
                          @click="copyLink(`#${slugMe(title)}`)"
                          ><v-icon small>
                            mdi-link
                          </v-icon></v-btn
                        ></h2
                      ></div
                    >
                  </li>
                </v-col>
              </v-row>
            </ul>
            <v-row wrap v-if="getFeatures" class="py-4">
              <v-col
                cols="12"
                v-for="({ title, description }, i) of getFeatures"
                :key="title"
              >
                <div class="feature-name py-1">
                  <h2
                    :id="slugMe(title)"
                    class="display-1 mb-2 font-weight-bold"
                    >{{ i + 1 }}. {{ title }}</h2
                  >
                  <v-divider></v-divider>
                </div>

                <Markdown
                  v-if="description.length"
                  class="title"
                  :source="description"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" class="text-center">
            <v-btn
              dark
              depressed
              rounded
              class="my-2 v-size--jumbo font-weight-bold"
              color="blue darken-1"
              to="/signup"
              >{{
                $mq === 'xs' ? 'Start Free Trial' : 'Start My Free Trial'
              }}</v-btn
            >
          </v-col>
          <v-col cols="12" class="text-center">
            <v-btn
              dark
              outlined
              rounded
              class="my-2 font-weight-bold"
              color="blue darken-1"
              to="/"
              large
              >Back to the Home Page</v-btn
            ></v-col
          >
        </v-row>
      </v-sheet>
    </v-col>
    <v-fab-transition>
      <v-btn
        v-if="showFAB"
        color="deep-orange"
        dark
        fixed
        bottom
        right
        fab
        v-scroll-to="'body'"
      >
        <v-icon>mdi-arrow-up</v-icon>
      </v-btn>
    </v-fab-transition>
    <v-snackbar
      v-model="showLinkCopiedMessage"
      timeout="-1"
      color="blue darken-1"
    >
      <h3 class="text-center">
        Link Copied to the Clipboard
      </h3>
    </v-snackbar>
  </v-row>
</template>

<script>
import Markdown from '@/components/Markdown'
import { mapGetters, mapActions } from 'vuex'
import slugify from 'slugify'

export default {
  name: 'FeaturesView',
  meta() {
    return {
      title: 'Minimart | Features - Take a Tour of the Platform',
      meta: [
        {
          name: 'description',
          content: 'Stay organized and get started in minutes.',
        },
      ],
    }
  },
  components: { Markdown },
  computed: {
    ...mapGetters('shop', ['getFeatures', 'getShopDetail']),
    featureTitles() {
      return this.features.map((f) => f.title)
    }, // featureTitles
    features() {
      return this.getFeatures || []
    },
  },
  data() {
    return {
      showFAB: false,
      hoveringTitle: null,
      canCopy: false,
      showLinkCopiedMessage: false,
      visibleFeatures: 0,
      featureInterval: null,
    }
  },
  methods: {
    ...mapActions('shop', ['fetchFeatures']),
    onIntersect(entries) {
      // More information about these options
      // is located here: https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
      const isIntersecting = entries[0].intersectionRatio >= 0.5
      this.showFAB = !isIntersecting
      if (
        isIntersecting &&
        this.features.length &&
        this.featureInterval === null
      ) {
        this.setFeaturesTimeout()
      } else if (
        !isIntersecting &&
        this.features.length &&
        this.visibleFeatures >= this.features.length
      ) {
        clearInterval(this.featureInterval)
        this.visibleFeatures = 0
        this.setFeaturesTimeout()
      }
    },
    setFeaturesTimeout() {
      setTimeout(() => {
        if (this.featureInterval) {
          clearInterval(this.featureInterval)
        }
        this.featureInterval = setInterval(() => {
          this.visibleFeatures++
          if (this.visibleFeatures >= this.features.length) {
            clearInterval(this.featureInterval)
          }
        }, 400)
      }, 400)
    }, // setFeaturesTimeout
    slugMe(string) {
      return slugify(string, { lower: true, remove: /[*+~.&()'"!:@?\\/]/g })
    },
    async copyLink(hash) {
      let link = `${document.location.origin}${this.$route.path}${hash}`
      await navigator.clipboard.writeText(link)
      this.showLinkCopiedMessage = true
      setTimeout(() => {
        this.showLinkCopiedMessage = false
      }, 2000)
    },
  },
  async created() {
    this.canCopy = !!navigator.clipboard
    await this.fetchFeatures()
    if (this.$route.hash) {
      this.$scrollTo(this.$route.hash, 700, { offset: -105 })
    }
  },
}
</script>

<style lang="scss">
.features {
  ul {
    list-style-type: none;
    padding-left: 0;
    li {
      padding-left: 0px;
      a {
        text-decoration: none;
      }
    }
  }
  .feature-name {
    position: sticky;
    top: 56px;
    background: white;
    z-index: 3;
  }
  .feature-wrap {
    position: relative;
    .feature-question {
      position: sticky;
      top: 105px;
      background: white;
      z-index: 2;
    }
    .link-icon {
      position: absolute;
      top: 0.5em;
      left: -4px;
      transform: translateX(-100%);
    }
  }
}
.feature-item {
  transition: all 0.7s ease-out;
  &.hide {
    opacity: 0;
    transform: translateX(-20%);
  }
}
</style>
